import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card, Row, Col, Statistic, Table, Tag, Select, Typography, Space, Button, Empty, Spin, message, Image,
} from "antd";
import { ReloadOutlined, TrophyOutlined } from "@ant-design/icons";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";
import { getSelectionMonitor } from "../network/api";

const { Title, Text } = Typography;

// Live monitor for compliance image selection. Polls /picture/compliance/monitor and renders the
// headline totals, per-day counts, SKU distribution, gate drop-off and the recent-selections feed.
const REFRESH_MS = 30000;
const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

// Gate drop-off is ordered as the gates actually run, so the chart reads like the funnel.
const DROP_ORDER = [
  ["eligible", "eligible", "#2f8a4e"],
  ["no_chiller", "no chiller", "#b23a33"],
  ["low_confidence", "low confidence", "#c0603a"],
  ["blurry", "blurry", "#a9781f"],
  ["person", "person", "#b23a33"],
  ["mask", "mask / fill", "#8a6d3b"],
  ["cut_off", "cut off", "#7b6cc4"],
  ["other", "other", "#8593a0"],
];

const SelectionMonitor = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const d = await getSelectionMonitor(days);
      setData(d);
      setUpdatedAt(new Date());
    } catch (e) {
      if (!silent) message.error("Could not load the selection monitor");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load(false);
    timer.current = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [load]);

  const t = data?.totals || {};
  const dropData = data
    ? DROP_ORDER.map(([k, label, color]) => ({ key: k, label, count: data.gateDropoff?.[k] || 0, color }))
        .filter((r) => r.count > 0 || r.key === "eligible")
    : [];

  const recentCols = [
    {
      // What Planogram actually received — the delivered chiller crop when there was one, else the
      // full frame. Lets you review the master here without opening Selections. O104.
      title: "Delivered", key: "img", width: 84,
      render: (_, r) => {
        const src = r.deliveredCropUrl || r.absolutePath;
        return src ? (
          <Image src={src} alt="delivered" width={64} height={48} style={{ objectFit: "cover", borderRadius: 4 }} preview={{ src }} />
        ) : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
      },
    },
    {
      title: "Device", key: "device",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.deviceId || "—"}</Text>
          {r.deviceTitle && <Text type="secondary" style={{ fontSize: 12 }}>{r.deviceTitle}</Text>}
        </Space>
      ),
    },
    { title: "Chiller", dataIndex: "chillerConfidence", key: "chiller", render: (v) => pct(v) },
    { title: "SKUs", dataIndex: "skuTotal", key: "sku", render: (v) => (v != null ? <Tag color="blue">{v}</Tag> : <Text type="secondary">—</Text>) },
    { title: "Person", dataIndex: "humanMaxScore", key: "person", render: (v) => (v != null ? v.toFixed(2) : "—") },
    { title: "Cropped", dataIndex: "cropped", key: "cropped", render: (v) => (v ? <Tag color="green">cropped</Tag> : <Text type="secondary">full</Text>) },
    { title: "Why", dataIndex: "reason", key: "reason", ellipsis: true, render: (v) => <Text style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "Selected", dataIndex: "selectedAt", key: "at", render: (v) => (v ? new Date(v).toLocaleString() : "—") },
  ];

  return (
    <div style={{ padding: 4 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><TrophyOutlined /> Selection Monitor</Title>
          <Text type="secondary">
            Live compliance image selection{updatedAt ? ` · updated ${updatedAt.toLocaleTimeString()}` : ""} · auto-refresh 30s
          </Text>
        </Col>
        <Col>
          <Space>
            <Select
              value={days}
              onChange={setDays}
              options={[{ value: 1, label: "Last 24h" }, { value: 7, label: "Last 7 days" }, { value: 14, label: "Last 14 days" }, { value: 30, label: "Last 30 days" }]}
              style={{ width: 140 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => load(false)}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {loading && !data ? (
        <div style={{ textAlign: "center", padding: 80 }}><Spin size="large" /></div>
      ) : !data ? (
        <Empty description="No data" />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}><Card size="small"><Statistic title="Selected · 24h" value={t.selected24h ?? 0} /></Card></Col>
            <Col xs={12} md={6}><Card size="small"><Statistic title={`Selected · ${days}d`} value={t.selectedWindow ?? 0} /></Card></Col>
            <Col xs={12} md={6}><Card size="small"><Statistic title="Avg SKUs / master" value={t.avgSku ?? "—"} /></Card></Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Cropped to chiller" value={t.cropped ?? 0}
                  suffix={t.selectedWindow ? <Text type="secondary" style={{ fontSize: 13 }}>/ {t.selectedWindow}</Text> : null} />
              </Card>
            </Col>
          </Row>
          {t.withPerson > 0 && (
            <Row style={{ marginTop: 12 }}>
              <Col span={24}>
                <Card size="small" style={{ borderColor: "#e0a", background: "rgba(200,40,40,0.06)" }}>
                  <Text type="danger" strong>{t.withPerson}</Text>{" "}
                  <Text type="secondary">selected master(s) in this window had a detected person ≥0.25 — investigate if recent.</Text>
                </Card>
              </Col>
            </Row>
          )}

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={14}>
              <Card size="small" title="Selections per day">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.perDay} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="selected" name="selected" fill="#128a99" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="noSelection" name="no selection" fill="#c9922b" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="withPerson" name="had person" fill="#b23a33" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} lg={10}>
              <Card size="small" title="SKU count distribution (selected masters)">
                {data.skuDist?.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.skuDist} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" name="masters" fill="#128a99" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="No SKU-scored selections yet (enable SKU pre-select)" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={10}>
              <Card size="small" title="Gate drop-off (weighed candidates)">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart layout="vertical" data={dropData} margin={{ top: 8, right: 16, left: 24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={90} />
                    <Tooltip />
                    <Bar dataKey="count" name="frames" radius={[0, 3, 3, 0]}>
                      {dropData.map((d) => <Cell key={d.key} fill={d.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} lg={14}>
              <Card size="small" title="Recent selections">
                <Table
                  rowKey={(r, i) => `${r.deviceId}-${r.selectedAt}-${i}`}
                  columns={recentCols}
                  dataSource={data.recent || []}
                  size="small"
                  pagination={{ pageSize: 8, size: "small" }}
                  scroll={{ x: 720 }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default SelectionMonitor;
